import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { keyboardUtils } from '@ba-ui-toolkit/utils';

const { getKeyName, keyNames } = keyboardUtils;

/**
 * Forked from react-virtualized project: https://github.com/bvaughn/react-virtualized
 * This HOC decorates a virtualized component and responds to arrow-key events by scrolling one row or column at a time.
 */
export default class ArrowKeyStepper extends PureComponent {
	static propTypes = {
		isControlled: PropTypes.bool,
		onScrollToChange: PropTypes.func,
		scrollToColumn: PropTypes.number,
		scrollToRow: PropTypes.number,
		className: PropTypes.string,
		children: PropTypes.func,
		columnCount: PropTypes.number,
		rowCount: PropTypes.number,
		fixedColumns: PropTypes.number,
		fixedRows: PropTypes.number,
		onFixedCornerCellMenu: PropTypes.func,
		onFixedRowCellMenu: PropTypes.func,
		style: PropTypes.object,
	};

	static defaultProps = {
		isControlled: false,
		scrollToColumn: 0,
		scrollToRow: 0,
		className: '',
		style: {},
	};

	_columnStartIndex = 0;
	_columnStopIndex = 0;
	_rowStartIndex = 0;
	_rowStopIndex = 0;

	_registeredChild = null;

	constructor(props) {
		super(props);

		this.state = {
			scrollToColumn: props.scrollToColumn,
			scrollToRow: props.scrollToRow,
		};
	}

	componentWillReceiveProps(nextProps) {
		if (this.props.isControlled) {
			return;
		}

		const { scrollToColumn, scrollToRow } = nextProps;

		const { scrollToColumn: prevScrollToColumn, scrollToRow: prevScrollToRow } = this.props;

		if (prevScrollToColumn !== scrollToColumn && prevScrollToRow !== scrollToRow) {
			this.setState({
				scrollToColumn,
				scrollToRow,
			});
		} else if (prevScrollToColumn !== scrollToColumn) {
			this.setState({ scrollToColumn });
		} else if (prevScrollToRow !== scrollToRow) {
			this.setState({ scrollToRow });
		}
	}

	setScrollIndexes({ scrollToColumn, scrollToRow }) {
		this.setState({
			scrollToRow,
			scrollToColumn,
		});
	}

	render() {
		const { className, children, style } = this.props;
		const { scrollToColumn, scrollToRow } = this._getScrollState();

		return (
			<div className={className} onKeyDown={this._onKeyDown} style={style}>
				{children({
					onSectionRendered: this._onSectionRendered,
					scrollToColumn,
					scrollToRow,
					registerChild: this._registerChild,
				})}
			</div>
		);
	}

	_onKeyDown = event => {
		const {
			columnCount,
			rowCount,
			onFixedCornerCellMenu,
			onFixedRowCellMenu,
			fixedRows,
			fixedColumns,
		} = this.props;

		// Don't do anything if there's no data
		// Also considered the case where the row count is adjusted
		if (rowCount === fixedRows || rowCount === 0) return;

		const {
			scrollToColumn: scrollToColumnPrevious,
			scrollToRow: scrollToRowPrevious,
		} = this._getScrollState();

		let { scrollToColumn, scrollToRow } = this._getScrollState();

		const { LEFT, RIGHT, UP, DOWN, F10, END, HOME } = keyNames;
		const keyName = getKeyName(event);

		switch (keyName) {
			case DOWN:
				scrollToRow = Math.min(scrollToRow + 1, rowCount - 1);
				break;
			case LEFT:
				scrollToColumn = Math.max(scrollToColumn - 1, 0);
				break;
			case RIGHT:
				scrollToColumn = Math.min(scrollToColumn + 1, columnCount - 1);
				break;
			case UP:
				scrollToRow = Math.max(scrollToRow - 1, 0);
				break;
			case HOME:
				scrollToRow = fixedRows;
				break;
			case END:
				scrollToRow = rowCount - 1;
				break;
			default:
				break;
		}

		if (scrollToColumn !== scrollToColumnPrevious || scrollToRow !== scrollToRowPrevious) {
			event.preventDefault();

			if (scrollToColumnPrevious < 0 || scrollToRowPrevious < 0) {
				// If out of bounds and key down event then set the focused cell to 0,0
				this._updateScrollState({ scrollToColumn: 0, scrollToRow: 0 });
			} else {
				this._updateScrollState({ scrollToColumn, scrollToRow });
			}
		} else if (keyName === F10) {
			if (event.shiftKey) {
				event.preventDefault();
				const { x, y } = ReactDOM.findDOMNode(
					this._registeredChild
				).getBoundingClientRect();

				if (scrollToRow < fixedRows && scrollToColumn < fixedColumns) {
					// If in the corner grid
					onFixedCornerCellMenu && onFixedCornerCellMenu(x, y, 0, 0);
				} else if (
					scrollToRow === 0 &&
					scrollToColumn >= fixedColumns &&
					onFixedRowCellMenu
				) {
					// If in row header
					const gridCellOffset = this._registeredChild._bottomRightGrid.getOffsetForCell({
						alignment: 'start',
						columnIndex: scrollToColumn,
						rowIndex: scrollToRow,
					});
					const scrollContainer = ReactDOM.findDOMNode(
						this._registeredChild._bottomRightGrid
					);

					onFixedRowCellMenu &&
						onFixedRowCellMenu(
							gridCellOffset.scrollLeft + x - scrollContainer.scrollLeft,
							y,
							scrollToRow,
							scrollToColumn - fixedColumns
						);
				}
			}
		}
	};

	_onSectionRendered = ({ columnStartIndex, columnStopIndex, rowStartIndex, rowStopIndex }) => {
		this._columnStartIndex = columnStartIndex;
		this._columnStopIndex = columnStopIndex;
		this._rowStartIndex = rowStartIndex;
		this._rowStopIndex = rowStopIndex;
	};

	_getScrollState = () => (this.props.isControlled ? this.props : this.state);

	_updateScrollState = ({ scrollToColumn, scrollToRow }) => {
		const { isControlled, onScrollToChange } = this.props;

		if (typeof onScrollToChange === 'function') {
			onScrollToChange({ scrollToColumn, scrollToRow });
		}

		if (!isControlled) {
			this.setState({ scrollToColumn, scrollToRow });
		}
	};

	_registerChild = child => {
		this._registeredChild = child;
	};
}
